import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { networkCreateGamePreview, networkUpdateGamePreview, useFetchGamePreviewTypeList } from 'src/network/game-management';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';
import GeneralMediaUploader from 'src/components/general/GeneralMediaUploader';

const GamePreviewForm = () => {
  const { gameId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { modal, closeModal } = useFormsModalStore();

  const [gamePreviewTypes, setGamePreviewTypes] = useState([]);
  const [selectedGamePreviewType, setSelectedGamePreviewType] = useState('');

  const gamePreviewDetails = modal?.data;
  const defaultValues = getDefaultValues(gamePreviewDetails);
  const isEditMode = gamePreviewDetails;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    trigger,
  } = methods;

  const { data: gamePreviewTypeResult } = useFetchGamePreviewTypeList();

  useEffect(() => {
    if (gamePreviewTypeResult) {
      const gamePreviewTypes = generateSelectMenuItems(gamePreviewTypeResult?.data?.items, 'GamePreviewTypeLable', 'GamePreviewTypeID');
      setGamePreviewTypes(gamePreviewTypes);
      setSelectedGamePreviewType(gamePreviewDetails?.GamePreviewGamePreviewTypeID || gamePreviewTypes?.[0]?.id);
    }
  }, [gamePreviewTypeResult]);

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GamePreviewGameID: +gameId,
      GamePreviewGamePreviewTypeID: selectedGamePreviewType,
      GamePreviewFrameMode: 0,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGamePreview(gamePreviewDetails?.GamePreviewID, PAYLOAD);
        if (![0, -1]?.includes(res.data?.items?.ResultID)) {
          enqueueSnackbar('پیشنمایش موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries(['GAME_INFO', gameId]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGamePreview(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پیشنمایش موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries(['GAME_INFO', gameId]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
        setSelectedGamePreviewType(gamePreviewTypes?.[0]?.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="GamePreviewTitle" type="text" label="عنوان پیشنمایش" />

              <GeneralSelectMenu
                label="نوع پیشنمایش"
                name="GamePreviewGamePreviewTypeID"
                options={gamePreviewTypes}
                value={selectedGamePreviewType}
                onChangeValue={setSelectedGamePreviewType}
              />

              <GeneralUpload
                id={'game-review-url-image'}
                name="GamePreviewUrlImage"
                label="کاور نمایش"
                afterChangeAction={(res) => {
                  setValue('GamePreviewUrlImage', res);
                  trigger('GamePreviewUrlImage');
                }}
              />

              <GeneralMediaUploader
                label="فایل ویدیویی"
                id="game-preview-url-mp4"
                name="GamePreviewUrlMp4"
                afterChangeAction={(res) => {
                  setValue('GamePreviewUrlMp4', res);
                  trigger('GamePreviewUrlMp4');
                }}
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GamePreviewForm;
